import React, { useState, useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import { FaDownload, FaFileCsv, FaFilePdf, FaFileExcel, FaTrash, FaCalculator, FaBan } from "react-icons/fa";
import { MdAssignment } from "react-icons/md";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { BillingUtils } from '../Services/billingService';

const BillingActionButton = ({ 
  selectedActivities = [], 
  onBulkReprice,
  onGenerateSalesOrders,
  onMarkNonBillable,
  onBulkDelete,
  refreshData,
  currentUserRoles = [],
  isProcessingBulkAction = false
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRepricing, setIsRepricing] = useState(false);

  const exportToPDF = (activities) => {
    if (activities.length === 0) return;
  
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    
    doc.setFontSize(16);
    doc.text("Billing Activities Report", doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' });
  
    const allKeys = new Set();
    activities.forEach(activity => {
      Object.keys(activity).forEach(key => {
        if (!key.startsWith('_') && key !== '__v') {
          allKeys.add(key);
        }
      });
    });
  
    const preferredOrder = [
      'id', 'patient_name', 'doctor_name', 'center_name', 'payer_name',
      'performed_at', 'activity_type', 'description', 'status', 
      'gross_amount', 'net_amount', 'tax_amount', 'billed_net', 'remaining_net',
      'has_authorization', 'authorization_code', 'created_at'
    ];
    
    const sortedKeys = Array.from(allKeys).sort((a, b) => {
      const indexA = preferredOrder.indexOf(a);
      const indexB = preferredOrder.indexOf(b);
      
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
  
    const formattedActivities = activities.map(activity => {
      const formatted = {};
      
      sortedKeys.forEach(key => {
        if (!activity.hasOwnProperty(key)) {
          formatted[key] = '';
          return;
        }
        
        let value = activity[key];
        
        if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}/)) {
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              value = date.toLocaleDateString();
            }
          } catch (e) {}
        }
        
        if (key.includes('amount') && typeof value === 'number') {
          value = BillingUtils.formatCurrency(value);
        }
        
        if (typeof value === 'boolean') {
          value = value ? 'Yes' : 'No';
        }
        
        if (value !== null && typeof value === 'object') {
          try {
            value = JSON.stringify(value)
              .replace(/[{}"[\]]/g, '')
              .replace(/,/g, ', ')
              .trim();
          } catch (e) {
            value = String(value);
          }
        }
        
        value = value !== null && value !== undefined ? String(value) : '';
        formatted[key] = value;
      });
      
      return formatted;
    });
  
    const formattedHeaders = sortedKeys.map(key => {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    });
  
    const tableColumns = formattedHeaders;
    const tableRows = formattedActivities.map(activity => 
      sortedKeys.map(key => activity[key] || '')
    );
  
    const columnWidths = {};
    sortedKeys.forEach((key, index) => {
      let columnWidth;
      
      if (key === 'id' || key === '_id' || key.endsWith('Id')) {
        columnWidth = 25;
      } else if (key.includes('name') || key.includes('Name')) {
        columnWidth = 30;
      } else if (key === 'description') {
        columnWidth = 45;
      } else if (key.includes('amount')) {
        columnWidth = 20;
      } else {
        columnWidth = 25;
      }
      
      columnWidths[index] = { cellWidth: columnWidth };
    });
  
    const pageWidth = doc.internal.pageSize.getWidth() - 20;
    const totalWidthUnits = Object.values(columnWidths).reduce((sum, col) => sum + col.cellWidth, 0);
    const scaleRatio = pageWidth / totalWidthUnits;
    
    Object.keys(columnWidths).forEach(index => {
      columnWidths[index].cellWidth = columnWidths[index].cellWidth * scaleRatio;
    });
  
    autoTable(doc, {
      head: [tableColumns],
      body: tableRows,
      startY: 20,
      styles: {
        fontSize: 8,
        cellPadding: 2,
        overflow: 'linebreak',
        lineColor: [200, 200, 200],
        lineWidth: 0.1,
        halign: 'left',
        valign: 'middle',
      },
      headStyles: {
        fillColor: [220, 220, 220],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        halign: 'center',
      },
      columnStyles: columnWidths,
      margin: { top: 20, right: 10, bottom: 15, left: 10 },
      tableWidth: 'auto',
      showHead: 'everyPage',
      didDrawPage: (data) => {
        const pageCount = doc.internal.getNumberOfPages();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text(
          `Page ${data.pageNumber} of ${pageCount}`,
          pageWidth / 2,
          pageHeight - 5,
          { align: 'center' }
        );
        
        const dateText = `Generated: ${new Date().toLocaleString()} | Total Records: ${tableRows.length}`;
        doc.text(dateText, 10, pageHeight - 5);
        
        if (data.pageNumber > 1) {
          doc.setFontSize(14);
          doc.setTextColor(0);
          doc.text("Billing Activities Report (Continued)", pageWidth / 2, 10, { align: 'center' });
        }
      }
    });
  
    const date = new Date().toISOString().slice(0, 10);
    doc.save(`billing_activities_${date}.pdf`);
  };
  
  const exportToCSV = (activities) => {
    if (activities.length === 0) return;
  
    const preferredOrder = [
      'id', 'patient_name', 'doctor_name', 'center_name', 'payer_name',
      'performed_at', 'activity_type', 'description', 'status', 
      'gross_amount', 'net_amount', 'tax_amount', 'billed_net', 'remaining_net',
      'has_authorization', 'authorization_code', 'created_at'
    ];
  
    const allKeys = new Set();
    activities.forEach(activity => {
      Object.keys(activity).forEach(key => {
        if (!key.startsWith('_') && key !== '__v') {
          allKeys.add(key);
        }
      });
    });
  
    const sortedKeys = Array.from(allKeys).sort((a, b) => {
      const indexA = preferredOrder.indexOf(a);
      const indexB = preferredOrder.indexOf(b);
      
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
  
    const processedActivities = activities.map(activity => {
      const processed = {};
      
      sortedKeys.forEach(key => {
        if (!activity.hasOwnProperty(key)) {
          processed[key] = '';
          return;
        }
        
        let value = activity[key];
        
        if (value && typeof value === 'string' && 
            (value.match(/^\d{4}-\d{2}-\d{2}T/) || value.match(/^\d{4}-\d{2}-\d{2}/))) {
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              processed[key] = date.toLocaleDateString();
            } else {
              processed[key] = value;
            }
          } catch (e) {
            processed[key] = value;
          }
        } 
        else if (key.includes('amount') && typeof value === 'number') {
          processed[key] = BillingUtils.formatCurrency(value);
        }
        else if (typeof value === 'object' && value !== null) {
          try {
            processed[key] = JSON.stringify(value)
              .replace(/[{}"[\]]/g, '')
              .replace(/,/g, ', ')
              .trim();
          } catch (e) {
            processed[key] = String(value);
          }
        }
        else if (typeof value === 'boolean') {
          processed[key] = value ? 'Yes' : 'No';
        }
        else if (value === undefined || value === null) {
          processed[key] = '';
        }
        else {
          processed[key] = value;
        }
      });
      
      return processed;
    });
  
    const headers = sortedKeys.map(key => {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    });
    
    const headerRow = headers
      .map(header => `"${header}"`)
      .join(',');
    
    const dataRows = processedActivities.map(activity => 
      sortedKeys.map(key => {
        const value = activity[key];
        if (value === null || value === undefined || value === '') return '""';
        return `"${String(value).replace(/"/g, '""')}"`;
      }).join(',')
    ).join('\n');
    
    const csvContent = `data:text/csv;charset=utf-8,${headerRow}\n${dataRows}`;
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    
    const date = new Date().toISOString().slice(0, 10);
    link.setAttribute("download", `billing_activities_${date}.csv`);
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const exportToExcel = (activities) => {
    if (activities.length === 0) return;
  
    const processedActivities = activities.map(activity => {
      const processed = {};
      
      Object.keys(activity).forEach(key => {
        if (activity[key] && typeof activity[key] === 'string' && 
            activity[key].match(/^\d{4}-\d{2}-\d{2}T/)) {
          processed[key] = activity[key];
        } 
        else if (typeof activity[key] === 'object' && activity[key] !== null) {
          processed[key] = JSON.stringify(activity[key]);
        }
        else {
          processed[key] = activity[key];
        }
      });
      
      return processed;
    });
  
    const worksheet = XLSX.utils.json_to_sheet(processedActivities);
    
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    
    const columnWidths = [];
    for (let C = range.s.c; C <= range.e.c; ++C) {
      let maxColumnWidth = 10;
      
      for (let R = range.s.r; R <= range.e.r; ++R) {
        const cellAddress = XLSX.utils.encode_cell({r: R, c: C});
        const cell = worksheet[cellAddress];
        
        if (!cell) continue;
        
        const cellContent = String(cell.v || '');
        const estimatedWidth = Math.min(50, cellContent.length * 1.2);
        maxColumnWidth = Math.max(maxColumnWidth, estimatedWidth);
      }
      
      columnWidths[C] = { width: maxColumnWidth };
    }
    
    worksheet['!cols'] = columnWidths;
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Billing Activities");
    
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const headerAddress = XLSX.utils.encode_cell({r: 0, c: C});
      if (!worksheet[headerAddress]) continue;
      
      const headerText = worksheet[headerAddress].v;
      worksheet[headerAddress].v = typeof headerText === 'string' ? 
        headerText.charAt(0).toUpperCase() + headerText.slice(1) : 
        headerText;
    }
    
    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `billing_activities_${date}.xlsx`);
  };
  
  const printActivities = (activities) => {
    if (activities.length === 0) return;
  
    const preferredOrder = [
      'id', 'patient_name', 'doctor_name', 'center_name', 'payer_name',
      'performed_at', 'activity_type', 'description', 'status', 
      'gross_amount', 'net_amount', 'tax_amount', 'billed_net', 'remaining_net'
    ];
  
    const allKeys = new Set();
    activities.forEach(activity => {
      Object.keys(activity).forEach(key => {
        if (!key.startsWith('_') && key !== '__v') {
          allKeys.add(key);
        }
      });
    });
  
    const sortedKeys = Array.from(allKeys).sort((a, b) => {
      const indexA = preferredOrder.indexOf(a);
      const indexB = preferredOrder.indexOf(b);
      
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.localeCompare(b);
    });
  
    const processedData = activities.map(activity => {
      const processed = {};
      
      sortedKeys.forEach(key => {
        if (!activity.hasOwnProperty(key)) {
          processed[key] = '';
          return;
        }
        
        let value = activity[key];
        
        if (value && typeof value === 'string' && 
            (value.match(/^\d{4}-\d{2}-\d{2}T/) || value.match(/^\d{4}-\d{2}-\d{2}/))) {
          try {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              processed[key] = date.toLocaleDateString();
            } else {
              processed[key] = value;
            }
          } catch (e) {
            processed[key] = value;
          }
        } 
        else if (key.includes('amount') && typeof value === 'number') {
          processed[key] = BillingUtils.formatCurrency(value);
        }
        else if (typeof value === 'object' && value !== null) {
          try {
            processed[key] = JSON.stringify(value)
              .replace(/[{}"[\]]/g, '')
              .replace(/,/g, ', ')
              .trim();
          } catch (e) {
            processed[key] = String(value);
          }
        }
        else if (typeof value === 'boolean') {
          processed[key] = value ? 'Yes' : 'No';
        }
        else if (value === undefined || value === null) {
          processed[key] = '';
        }
        else {
          processed[key] = value;
        }
      });
      
      return processed;
    });
      
    const headers = sortedKeys.map(key => {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    });
    
    const printWindow = window.open('', 'PRINT', 'height=800,width=1200');
    
    const tableHTML = `
      <html>
        <head>
          <title>Billing Activities Report</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 0;
              padding: 20px;
            }
            h1 { 
              text-align: center; 
              margin-bottom: 20px;
            }
            .print-info {
              display: flex;
              justify-content: space-between;
              margin-bottom: 20px;
              font-size: 12px;
            }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-bottom: 20px;
              font-size: 11px;
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 6px; 
              text-align: left;
              vertical-align: top;
            }
            th { 
              background-color: #f2f2f2; 
              font-weight: bold;
              position: sticky;
              top: 0;
            }
            tr:nth-child(even) {
              background-color: #f9f9f9;
            }
            @page { 
              size: landscape; 
              margin: 0.5in;
            }
            thead { display: table-header-group; }
            tr { page-break-inside: avoid; }
            td.long-text {
              max-width: 200px;
              word-wrap: break-word;
            }
            ${sortedKeys.map((key, index) => {
              let width;
              if (key === 'id' || key === '_id' || key.endsWith('Id')) {
                width = '80px';
              } else if (key.includes('name') || key.includes('Name')) {
                width = '120px';
              } else if (key === 'description') {
                width = '200px; overflow-wrap: break-word;';
              } else if (key.includes('amount')) {
                width = '100px';
              } else if (key.includes('date') || key.includes('Date')) {
                width = '100px';
              } else {
                width = '100px';
              }
              return `.col-${index} { width: ${width}; }`;
            }).join('\n')}
          </style>
        </head>
        <body>
          <h1>Billing Activities Report</h1>
          <div class="print-info">
            <div>Total Items: ${processedData.length}</div>
            <div>Generated on: ${new Date().toLocaleString()}</div>
          </div>
          <table>
            <thead>
              <tr>
                ${headers.map((header, index) => `<th class="col-${index}">${header}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${processedData.map(row => 
                `<tr>
                  ${sortedKeys.map((key, index) => {
                    const value = row[key] !== null && row[key] !== undefined ? row[key] : '';
                    const className = (key === 'description') ? 'long-text' : '';
                    return `<td class="col-${index} ${className}">${value}</td>`;
                  }).join('')}
                </tr>`
              ).join('')}
            </tbody>
          </table>
          <script>
            window.onload = function() {
              setTimeout(function() {
                window.print();
              }, 500);
            };
          </script>
        </body>
      </html>
    `;
  
    printWindow.document.write(tableHTML);
    printWindow.document.close();
    printWindow.focus();
  };

  const handleBulkReprice = async () => {
    if (selectedActivities.length === 0) {
      alert('Please select activities to reprice');
      return;
    }
    
    if (window.confirm(`Reprice ${selectedActivities.length} selected activities?`)) {
      setIsRepricing(true);
      try {
        await onBulkReprice(selectedActivities);
        alert(`Successfully repriced ${selectedActivities.length} activities`);
        
        if (refreshData) {
          refreshData();
        } else {
          window.location.reload();
        }
      } catch (error) {
        console.error('Error repricing activities:', error);
        alert(`An unexpected error occurred: ${error.message}`);
      } finally {
        setIsRepricing(false);
      }
    }
  };

  const handleGenerateSalesOrders = () => {
    if (selectedActivities.length === 0) {
      alert('Please select activities to generate sales orders');
      return;
    }
    
    onGenerateSalesOrders(selectedActivities);
  };

  const handleBulkMarkNonBillable = () => {
    if (selectedActivities.length === 0) {
      alert('Please select activities to mark as non-billable');
      return;
    }
    
    if (window.confirm(`Mark ${selectedActivities.length} activities as non-billable?`)) {
      selectedActivities.forEach(activityId => {
        onMarkNonBillable(activityId, 'other', 'Bulk operation');
      });
      
      if (refreshData) {
        refreshData();
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedActivities.length === 0) return;
    
    const idList = selectedActivities.map(activity => {
      return activity._id || activity.id;
    }).join(", ");
    
    if (window.confirm(`Are you sure you want to delete the activities with IDs: ${idList}?`)) {
      setIsDeleting(true);
      
      try {
        await onBulkDelete(selectedActivities);
        alert(`Successfully deleted ${selectedActivities.length} activities`);
        
        if (refreshData) {
          refreshData();
        } else {
          window.location.reload();
        }
      } catch (error) {
        console.error('Error in bulk deletion:', error);
        alert(`An unexpected error occurred during deletion: ${error.message}`);
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const handleActionClick = (action) => {
    if (selectedActivities.length > 0) {
      console.log(`${action} clicked for ${selectedActivities.length} billing activities`);
      
      switch(action) {
        case "Print":
          printActivities(selectedActivities);
          break;
        case "Export CSV":
          exportToCSV(selectedActivities);
          break;
        case "Export PDF":
          exportToPDF(selectedActivities);
          break;
        case "Export Excel":
          exportToExcel(selectedActivities);
          break;
        case "Reprice":
          handleBulkReprice();
          break;
        case "Generate Sales Orders":
          handleGenerateSalesOrders();
          break;
        case "Mark Non-Billable":
          handleBulkMarkNonBillable();
          break;
        case "Delete":
          handleBulkDelete();
          break;
        default:
          console.warn(`Unknown action: ${action}`);
          break;
      }
    }
  };

  const canReprice = BillingUtils.canReprice && BillingUtils.canReprice(currentUserRoles);
  const canGenerateSalesOrders = BillingUtils.canGenerateSalesOrders && BillingUtils.canGenerateSalesOrders(currentUserRoles);
  const canMarkNonBillable = BillingUtils.canMarkNonBillable && BillingUtils.canMarkNonBillable(currentUserRoles);
  const canDelete = BillingUtils.hasPermission && BillingUtils.hasPermission(currentUserRoles, ['admin_clinica']);

  return (
    <div className="flex-grow-sm-0">
      <Dropdown>
        <Dropdown.Toggle
          id="billing-action-dropdown"
          className={`btn btn-gray text-start w-100 d-flex align-items-center justify-content-between px-4 py-2 border-0 rounded-3 shadow-sm dropdownbtn`}
          disabled={isDeleting || isRepricing || isProcessingBulkAction}
        >
          <span>
            {isDeleting ? 'Deleting...' : 
             isRepricing ? 'Repricing...' : 
             isProcessingBulkAction ? 'Processing...' :
             `Action${selectedActivities.length > 0 ? ` (${selectedActivities.length})` : ''}`}
          </span>
        </Dropdown.Toggle>

        <Dropdown.Menu className="dropdown-menu w-100 bg-white fs-sm fw-semibold border-0 rounded-3 shadow-sm mt-2">
          <Dropdown.Item
            onClick={() => handleActionClick("Print")}
            className="dropdown-item d-flex align-items-center gap-2 py-2"
            disabled={selectedActivities.length === 0 || isDeleting || isRepricing || isProcessingBulkAction}
          >
            <FaDownload className="icon-sm" />
            <span>Print Activities</span>
          </Dropdown.Item>

          <Dropdown.Divider />

          <Dropdown.Item
            onClick={() => handleActionClick("Export CSV")}
            className="dropdown-item d-flex align-items-center gap-2 py-2"
            disabled={selectedActivities.length === 0 || isDeleting || isRepricing || isProcessingBulkAction}
          >
            <FaFileCsv className="icon-sm" />
            <span>Download CSV</span>
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => handleActionClick("Export PDF")}
            className="dropdown-item d-flex align-items-center gap-2 py-2"
            disabled={selectedActivities.length === 0 || isDeleting || isRepricing || isProcessingBulkAction}
          >
            <FaFilePdf className="icon-sm" />
            <span>Download PDF</span>
          </Dropdown.Item>
          <Dropdown.Item
            onClick={() => handleActionClick("Export Excel")}
            className="dropdown-item d-flex align-items-center gap-2 py-2"
            disabled={selectedActivities.length === 0 || isDeleting || isRepricing || isProcessingBulkAction}
          >
            <FaFileExcel className="icon-sm" />
            <span>Download Excel</span>
          </Dropdown.Item>

          {(canReprice || canGenerateSalesOrders || canMarkNonBillable) && <Dropdown.Divider />}

          {canReprice && (
            <Dropdown.Item
              onClick={() => handleActionClick("Reprice")}
              className="dropdown-item d-flex align-items-center gap-2 py-2"
              disabled={selectedActivities.length === 0 || isDeleting || isRepricing || isProcessingBulkAction}
            >
              <FaCalculator className="icon-sm" />
              <span>{isRepricing ? 'Repricing...' : 'Reprice Selected'}</span>
            </Dropdown.Item>
          )}

          {canGenerateSalesOrders && (
            <Dropdown.Item
              onClick={() => handleActionClick("Generate Sales Orders")}
              className="dropdown-item d-flex align-items-center gap-2 py-2"
              disabled={selectedActivities.length === 0 || isDeleting || isRepricing || isProcessingBulkAction}
            >
              <MdAssignment className="icon-sm" />
              <span>Generate Sales Orders</span>
            </Dropdown.Item>
          )}

          {canMarkNonBillable && (
            <Dropdown.Item
              onClick={() => handleActionClick("Mark Non-Billable")}
              className="dropdown-item d-flex align-items-center gap-2 py-2"
              disabled={selectedActivities.length === 0 || isDeleting || isRepricing || isProcessingBulkAction}
            >
              <FaBan className="icon-sm" />
              <span>Mark Non-Billable</span>
            </Dropdown.Item>
          )}

          {canDelete && (
            <>
              <Dropdown.Divider />
              <Dropdown.Item
                onClick={() => handleActionClick("Delete")}
                className="dropdown-item d-flex align-items-center gap-2 py-2"
                disabled={selectedActivities.length === 0 || isDeleting || isRepricing || isProcessingBulkAction}
              >
                <FaTrash className="icon-sm" />
                <span>{isDeleting ? 'Deleting...' : 'Delete Selected'}</span>
              </Dropdown.Item>
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default BillingActionButton;